import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormModal } from "@/components/form-modal";
import { ParticipantSearchInput } from "@/components/participant-search-input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Participant, JamNight, Performer } from "@shared/schema";

const formSchema = z.object({
  participants: z.array(z.object({
    participantId: z.number().optional(),
    name: z.string().min(1, "Participant name is required"),
  })).min(1, "At least one participant is required"),
  notes: z.string().optional(),
  priority: z.boolean().optional().default(false),
  jamNightId: z.number(),
});

interface AddGenericEventParticipantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jamNightId: number;
}

export default function AddGenericEventParticipantModal({ open, onOpenChange, jamNightId }: AddGenericEventParticipantModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [activeInputIndex, setActiveInputIndex] = useState<number>(-1);


  // Fetch jam night details to get organisation ID
  const { data: jamNight } = useQuery({
    queryKey: [`/api/events/${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/events/${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Fetch all participants for the organisation
  const { data: participants = [] } = useQuery({
    queryKey: [`/api/participants?organisationId=${jamNight?.organisationId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/participants?organisationId=${jamNight?.organisationId}`);
      return response.json();
    },
    enabled: open && !!jamNight?.organisationId,
  });

  // Fetch existing performers for this event to check for duplicates
  const { data: existingPerformers = [] } = useQuery({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Get set of participant IDs that are already added to this event
  const existingParticipantIds = useMemo(() => {
    return new Set<number>(
      existingPerformers
        .filter((p: Performer) => p.participantId && !p.removed)
        .map((p: Performer) => p.participantId)
    );
  }, [existingPerformers]);

  // Filter participants based on search term
  const filteredParticipants = useMemo(() => {
    if (!participants) return [];
    
    if (searchTerm.trim().length === 0) {
      return participants.sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
    }
    
    return participants
      .filter((participant: Participant) => 
        participant.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
  }, [participants, searchTerm]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      participants: [{ participantId: undefined, name: "" }],
      notes: "",
      priority: false,
      jamNightId,
    },
  });

  const createPerformersMutation = useMutation({
    mutationFn: async (formData: z.infer<typeof formSchema>) => {
      const promises = formData.participants.map(async (participant) => {
        const performerData = {
          participantId: participant.participantId,
          name: participant.name,
          notes: formData.notes,
          priority: formData.priority,
          jamNightId: formData.jamNightId,
        };
        const response = await apiRequest("POST", "/api/performers", performerData);
        return response.json();
      });
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/performers?jamNightId=${jamNightId}`] });
      // Also invalidate participants query to update autocomplete
      if (jamNight?.organisationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/participants?organisationId=${jamNight.organisationId}`] });
      }
      form.reset();
      onOpenChange(false);
      toast({
        title: "Success",
        description: `Added ${form.getValues('participants').length} participant(s) to the event`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to add participant(s)",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    createPerformersMutation.mutate(data);
  };

  const handleParticipantSelect = (participant: Participant, index: number) => {
    const currentParticipants = form.getValues('participants');
    currentParticipants[index] = {
      participantId: participant.id,
      name: participant.name,
    };
    form.setValue('participants', currentParticipants);
    setSearchTerm("");
    setShowDropdown(false);
    setActiveInputIndex(-1);
  };

  const handleSearchChange = (value: string, index: number) => {
    setSearchTerm(value);
    setActiveInputIndex(index);
    setShowDropdown(true);
    
    const currentParticipants = form.getValues('participants');
    currentParticipants[index] = {
      participantId: undefined,
      name: value,
    };
    form.setValue('participants', currentParticipants);
  };

  const addParticipant = () => {
    const currentParticipants = form.getValues('participants');
    form.setValue('participants', [...currentParticipants, { participantId: undefined, name: "" }]);
  };

  const removeParticipant = (index: number) => {
    const currentParticipants = form.getValues('participants');
    if (currentParticipants.length > 1) {
      currentParticipants.splice(index, 1);
      form.setValue('participants', currentParticipants);
    }
  };


  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setShowDropdown(false);
      setActiveInputIndex(-1);
      form.reset({
        participants: [{ participantId: undefined, name: "" }],
        notes: "",
        priority: false,
        jamNightId,
      });
    }
  }, [open, form, jamNightId]);

  const participantsFields = form.watch('participants');

  const MultiParticipantInput = ({ participantsFields }: { participantsFields: any[] }) => {
    return (
      <div className="space-y-3">
        <FormLabel>Participant Names</FormLabel>
        {participantsFields.map((field, index) => (
          <div key={index} className="flex gap-2 items-start">
            <div className="flex-1">
              <ParticipantSearchInput
                participants={filteredParticipants}
                existingParticipantIds={existingParticipantIds}
                value={index === activeInputIndex ? searchTerm : field.name}
                onSearchChange={(value) => handleSearchChange(value, index)}
                onParticipantSelect={(participant) => handleParticipantSelect(participant, index)}
                onClear={() => {
                  setSearchTerm("");
                  const currentParticipants = form.getValues('participants');
                  currentParticipants[index] = { participantId: undefined, name: "" };
                  form.setValue('participants', currentParticipants);
                }}
                placeholder="Search for participant or enter new name..."
              />
            </div>
            
            {participantsFields.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeParticipant(index)}
                className="mt-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addParticipant}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Another Participant
        </Button>
      </div>
    );
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Participants"
      form={form}
      onSubmit={onSubmit}
      isLoading={createPerformersMutation.isPending}
      submitLabel={createPerformersMutation.isPending ? "Adding..." : `Add ${participantsFields.length} Participant${participantsFields.length > 1 ? 's' : ''}`}
      width="md"
    >
      <MultiParticipantInput participantsFields={participantsFields} />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Priority Participants</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark these participants as priority
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add any notes about these participants..."
                      className="min-h-[80px] resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

    </FormModal>
  );
}